<template>
  <form
    class="flex h-[25rem] flex-1 flex-col bg-background-fg/5"
    @submit="onSubmit"
  >
    <BaseTabs
      v-model="currentTab"
      :tabs="tabs"
      class="self-start"
    />
    <div
      class="flex-1 overflow-y-auto border-y bg-background p-4"
    >
      <div
        v-show="currentTab === 'serviceCharge'"
        class="flex flex-col"
      >
        <FormInputPercentage
          label="Service Charge Percentage"
          name="serviceChargePercentage"
          class="max-w-[12.5rem]"
          :highlight="!!values.serviceChargePercentage"
          :min="0"
        />
        <FormInputPrice
          label="Service Charge Flat"
          name="serviceChargeFlat"
          class="max-w-[12.5rem]"
          :highlight="!!values.serviceChargeFlat"
          :min="0"
        />
        <div
          class="ml-5 mt-1 flex items-center text-xs text-info"
        >
          <IconInfo />
          <div class="ml-1">
            service charge will be added to the bill
            automatically before payment.
          </div>
        </div>

        <!-- service charge calcs -->
        <div class="mb-2 mt-5 self-center">
          <div
            class="flex items-center justify-between space-x-5"
          >
            <div class="text-xs text-background-fg/70">
              Subtotal
            </div>
            <div class="text-sm font-semibold">
              {{
                formatToMerchantCurrencyString(
                  totalOrderItemCostPence
                )
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between space-x-5"
          >
            <div
              class="text-xs font-semibold text-background-fg/70"
            >
              Service charge ({{
                values.serviceChargePercentage
              }}%)
            </div>

            <div class="text-sm font-semibold">
              {{
                formatToMerchantCurrencyString(
                  serviceChargePercentageAmount
                )
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between space-x-5"
          >
            <div
              class="text-xs font-semibold text-background-fg/70"
            >
              Service charge (Flat)
            </div>
            <div class="text-sm font-semibold">
              {{
                formatToMerchantCurrencyString(
                  values.serviceChargeFlat ?? 0
                )
              }}
            </div>
          </div>
          <div
            class="flex items-center justify-between space-x-5 border-t"
          >
            <div class="text-xs text-background-fg/70">
              Total
            </div>
            <div class="text-sm font-semibold">
              {{ formatToMerchantCurrencyString(total) }}
            </div>
          </div>
        </div>
      </div>

      <div v-show="currentTab === 'notes'">
        <TextAreaFormField label="Notes" name="note" />
      </div>
    </div>

    <div class="flex space-x-3 self-end p-2">
      <ActionButtonSecondary @click="onCancel">
        Cancel
      </ActionButtonSecondary>
      <ActionButtonPrimary type="submit">
        Confirm
      </ActionButtonPrimary>
    </div>
  </form>
</template>

<script setup lang="ts">
import {
  useConfigureOrderForm,
  type ConfigureOrderFormOutput,
  type ConfigureOrderFormInput,
} from "@/forms/configureOrderForm"
import type { TabOption } from "./BaseTabs.vue"

const { formatToMerchantCurrencyString } = useCurrency()

const { tenant } = useTenant()

const props = defineProps<{
  initialValues: ConfigureOrderFormInput
}>()

const emit = defineEmits<{
  (event: "submit", params: ConfigureOrderFormOutput): void
  (event: "cancel"): void
}>()

const { handleSubmit, values, resetForm } =
  useConfigureOrderForm(props.initialValues)

const { totalOrderItemCostPence } = useCurrentOrder()

const onSubmit = handleSubmit(async (params) => {
  emit("submit", params)
})

const onCancel = () => {
  resetForm()
  emit("cancel")
}

const currentTab = ref<"notes" | "serviceCharge">("notes")

const tabs: Array<TabOption<"serviceCharge" | "notes">> = [
  ...(tenant.isServiceChargeEnabled
    ? [
        {
          value: "serviceCharge",
          label: "Service Charge",
        } as const,
      ]
    : []),
  {
    value: "notes",
    label: "Notes",
  },
]

const serviceChargePercentageAmount = computed(() =>
  Math.round(
    ((values.serviceChargePercentage ?? 0) / 100) *
      totalOrderItemCostPence.value
  )
)

const serviceChargeTotal = computed(
  () =>
    serviceChargePercentageAmount.value +
    (values.serviceChargeFlat ?? 0)
)
const total = computed(() =>
  Math.max(
    serviceChargeTotal.value +
      totalOrderItemCostPence.value,
    0
  )
)
</script>
